'use client'

export const dynamic = 'force-dynamic'

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowLeft, Check, Inbox, Loader2, MessageCircle, Phone, Send, ShieldOff, Users, X } from 'lucide-react'

/* Masjid Connect — Requests inbox. Incoming (professional approves/declines) + Outgoing
   (requester sees status; approved → View contact via the single gated reveal endpoint).
   Whole surface 404s while masjidConnect is off (middleware /connect gate). Brand tokens. */

const NAVY = '#0B132B', GREEN = '#0E7C61', SLATE = '#5b6472', LINE = '#e6e4dc', BEIGE = '#F6F4EE'

type Incoming = { id: string; requesterName: string; message: string | null; status: string; createdAt: string; respondedAt: string | null }
type Outgoing = { id: string; status: string; targetName: string; category: string | null; contactAvailable: boolean }
type Contact = { name: string; whatsapp: string | null; phone: string | null }

export default function ConnectRequestsPage() {
  const [tab, setTab] = useState<'incoming' | 'outgoing' | 'blocked'>('incoming')
  return (
    <main style={{ maxWidth: 720, margin: '0 auto', padding: '28px 16px 64px' }}>
      <Link href="/connect" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: GREEN, fontSize: 13.5, fontWeight: 600, textDecoration: 'none', marginBottom: 14 }}><ArrowLeft size={15} /> Back to Find help</Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <span style={{ width: 40, height: 40, borderRadius: 12, background: '#eef6f3', display: 'grid', placeItems: 'center', color: GREEN }}><Inbox size={20} /></span>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: NAVY, margin: 0 }}>Connection requests</h1>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {(['incoming', 'outgoing', 'blocked'] as const).map((k) => (
          <button key={k} type="button" onClick={() => setTab(k)} style={{ border: `1.5px solid ${tab === k ? GREEN : LINE}`, background: tab === k ? GREEN : '#fff', color: tab === k ? '#fff' : NAVY, borderRadius: 999, padding: '8px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
            {k === 'incoming' ? 'Requests to me' : k === 'outgoing' ? 'My requests' : 'Blocked'}
          </button>
        ))}
      </div>

      {tab === 'incoming' ? <IncomingTab /> : tab === 'outgoing' ? <OutgoingTab /> : <BlockedTab />}
      <style jsx global>{`.spin{animation:crspin 1s linear infinite}@keyframes crspin{to{transform:rotate(360deg)}}`}</style>
    </main>
  )
}

function IncomingTab() {
  const [rows, setRows] = useState<Incoming[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [busy, setBusy] = useState('')

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      const res = await fetch('/api/connect/requests/incoming')
      if (!res.ok) return setStatus('error')
      const d = await res.json()
      setRows(Array.isArray(d.requests) ? d.requests : []); setStatus('ready')
    } catch { setStatus('error') }
  }, [])
  useEffect(() => { void load() }, [load])

  async function respond(id: string, action: 'approve' | 'decline') {
    setBusy(id)
    try {
      const res = await fetch(`/api/connect/requests/${id}/respond`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) })
      if (res.ok) await load()
    } finally { setBusy('') }
  }

  // Block the requester via the request id (their user id is never exposed to the client).
  async function block(r: Incoming) {
    if (!confirm(`Block ${r.requesterName}? They won’t be able to reach you on Masjid Connect.`)) return
    setBusy(r.id)
    try {
      const res = await fetch('/api/connect/blocks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ connectionRequestId: r.id }) })
      if (res.ok) setRows((rs) => rs.filter((x) => x.id !== r.id))
    } finally { setBusy('') }
  }

  if (status === 'loading') return <Spin />
  if (status === 'error') return <ErrorBox onRetry={load} />
  if (rows.length === 0) return <Empty icon={<Inbox size={26} />} title="No requests yet" body="When someone asks to connect with you, it’ll show here." />

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {rows.map((r) => (
        <div key={r.id} style={card}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={mono}>{r.requesterName.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'U'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{r.requesterName}</div>
              {r.message ? <p style={{ fontSize: 13.5, color: SLATE, lineHeight: 1.55, margin: '4px 0 0' }}>“{r.message}”</p> : <p style={{ fontSize: 13, color: '#9a988f', margin: '4px 0 0', fontStyle: 'italic' }}>No message</p>}
            </div>
            <StatusPill status={r.status} />
          </div>
          {r.status === 'pending' && (
            <>
              <p style={{ fontSize: 12.5, color: SLATE, lineHeight: 1.5, margin: '12px 0 0' }}>Approving shares your WhatsApp — or your account phone number if you haven’t added one — with them.</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button type="button" disabled={busy === r.id} onClick={() => void respond(r.id, 'approve')} style={{ ...btn, background: GREEN, color: '#fff', flex: 1 }}>{busy === r.id ? <Loader2 className="spin" size={15} /> : <Check size={15} />} Approve</button>
                <button type="button" disabled={busy === r.id} onClick={() => void respond(r.id, 'decline')} style={{ ...btn, background: '#fff', color: SLATE, border: `1.5px solid ${LINE}` }}><X size={15} /> Decline</button>
              </div>
            </>
          )}
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            <button type="button" disabled={busy === r.id} onClick={() => void block(r)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: SLATE, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><ShieldOff size={12} /> Block</button>
          </div>
        </div>
      ))}
    </div>
  )
}

function OutgoingTab() {
  const [rows, setRows] = useState<Outgoing[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [contact, setContact] = useState<{ id: string; data: Contact } | null>(null)
  const [busy, setBusy] = useState('')

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      const res = await fetch('/api/connect/requests/outgoing')
      if (!res.ok) return setStatus('error')
      const d = await res.json()
      setRows(Array.isArray(d.requests) ? d.requests : []); setStatus('ready')
    } catch { setStatus('error') }
  }, [])
  useEffect(() => { void load() }, [load])

  async function viewContact(id: string) {
    setBusy(id)
    try {
      const res = await fetch(`/api/connect/requests/${id}/contact`)
      const d = await res.json().catch(() => ({}))
      if (res.ok) setContact({ id, data: d })
    } finally { setBusy('') }
  }

  // Withdraw a still-pending request the user sent (requester-only, server-scoped). Drop the row on success.
  async function cancel(id: string) {
    setBusy(id)
    try {
      const res = await fetch('/api/connect/requests/withdraw', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId: id }) })
      if (res.ok) setRows((rs) => rs.filter((x) => x.id !== id))
    } finally { setBusy('') }
  }

  if (status === 'loading') return <Spin />
  if (status === 'error') return <ErrorBox onRetry={load} />
  if (rows.length === 0) return <Empty icon={<Send size={26} />} title="You haven’t sent any requests" body={<>Find someone who can help and send a request. <Link href="/connect" style={{ color: GREEN, fontWeight: 600 }}>Find help →</Link></>} />

  return (
    <>
      <div style={{ display: 'grid', gap: 12 }}>
        {rows.map((r) => (
          <div key={r.id} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={mono}>{r.targetName.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'U'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{r.targetName}</div>
                {r.category && <div style={{ fontSize: 12, color: SLATE, textTransform: 'capitalize', marginTop: 2 }}>{r.category}</div>}
              </div>
              <StatusPill status={r.status} />
            </div>
            {r.contactAvailable && (
              <button type="button" disabled={busy === r.id} onClick={() => void viewContact(r.id)} style={{ ...btn, background: GREEN, color: '#fff', marginTop: 14, width: '100%' }}>
                {busy === r.id ? <Loader2 className="spin" size={15} /> : <Phone size={15} />} View contact
              </button>
            )}
            {r.status === 'pending' && (
              <button type="button" disabled={busy === r.id} onClick={() => void cancel(r.id)} style={{ ...btn, background: '#fff', color: SLATE, border: '1px solid #dfe5e9', marginTop: 12, width: '100%' }}>
                {busy === r.id ? <Loader2 className="spin" size={15} /> : null} Cancel request
              </button>
            )}
          </div>
        ))}
      </div>
      {contact && <ContactModal data={contact.data} onClose={() => setContact(null)} />}
    </>
  )
}

function ContactModal({ data, onClose }: { data: Contact; onClose: () => void }) {
  const wa = data.whatsapp ? data.whatsapp.replace(/\D/g, '') : ''
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(11,19,43,.5)', display: 'grid', placeItems: 'center', padding: 16, zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, maxWidth: 400, width: '100%', padding: 24, position: 'relative' }}>
        <button type="button" aria-label="Close" onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: BEIGE, border: 'none', borderRadius: 999, width: 30, height: 30, display: 'grid', placeItems: 'center', cursor: 'pointer', color: SLATE }}><X size={16} /></button>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: GREEN, marginBottom: 4 }}>Contact approved</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: NAVY, marginBottom: 18 }}>{data.name}</div>
        {data.whatsapp && (
          <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#25D366', color: '#fff', borderRadius: 11, padding: '13px 16px', fontWeight: 700, fontSize: 15, textDecoration: 'none', marginBottom: 10 }}>
            <MessageCircle size={18} /> WhatsApp {data.whatsapp}
          </a>
        )}
        {data.phone && (
          <a href={`tel:${data.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 10, background: BEIGE, color: NAVY, borderRadius: 11, padding: '13px 16px', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
            <Phone size={18} /> {data.phone}
          </a>
        )}
        {!data.whatsapp && !data.phone && <p style={{ color: SLATE, fontSize: 14 }}>No contact details are on file for this profile.</p>}
        <p style={{ fontSize: 12, color: SLATE, textAlign: 'center', margin: '14px 0 0', lineHeight: 1.5 }}>Please reach out respectfully. They approved your request to help.</p>
      </div>
    </div>
  )
}

function BlockedTab() {
  const [rows, setRows] = useState<Array<{ blockedUserId: string; name: string }>>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [busy, setBusy] = useState('')

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      const res = await fetch('/api/connect/blocks')
      if (!res.ok) return setStatus('error')
      const d = await res.json()
      setRows(Array.isArray(d.blocks) ? d.blocks : []); setStatus('ready')
    } catch { setStatus('error') }
  }, [])
  useEffect(() => { void load() }, [load])

  async function unblock(id: string) {
    setBusy(id)
    try {
      const res = await fetch('/api/connect/blocks', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ blockedUserId: id }) })
      if (res.ok) setRows((rs) => rs.filter((x) => x.blockedUserId !== id))
    } finally { setBusy('') }
  }

  if (status === 'loading') return <Spin />
  if (status === 'error') return <ErrorBox onRetry={load} />
  if (rows.length === 0) return <Empty icon={<ShieldOff size={26} />} title="No blocked users" body="People you block won’t see you or be able to reach you on Masjid Connect. They’ll appear here." />

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((r) => (
        <div key={r.blockedUserId} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ ...mono, background: SLATE }}>{r.name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'U'}</span>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: NAVY }}>{r.name}</div>
          <button type="button" disabled={busy === r.blockedUserId} onClick={() => void unblock(r.blockedUserId)} style={{ ...btn, background: '#fff', color: NAVY, border: `1.5px solid ${LINE}` }}>{busy === r.blockedUserId ? <Loader2 className="spin" size={14} /> : null} Unblock</button>
        </div>
      ))}
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { bg: string; c: string; label: string }> = {
    pending: { bg: '#fff7e6', c: '#9a6b00', label: 'Pending' },
    approved: { bg: '#eaf7f1', c: GREEN, label: 'Approved' },
    declined: { bg: '#f3f3f1', c: SLATE, label: 'Declined' },
  }
  const s = map[status] || map.pending
  return <span style={{ background: s.bg, color: s.c, borderRadius: 999, padding: '4px 11px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', flex: 'none' }}>{s.label}</span>
}

const Spin = () => <div style={{ padding: 50, textAlign: 'center', color: SLATE }}><Loader2 className="spin" size={22} /></div>
function ErrorBox({ onRetry }: { onRetry: () => void }) {
  return <div style={{ textAlign: 'center', padding: 40 }}><p style={{ color: SLATE, marginBottom: 12 }}>Couldn’t load requests.</p><button type="button" onClick={onRetry} style={{ ...btn, background: GREEN, color: '#fff', margin: '0 auto' }}>Retry</button></div>
}
function Empty({ icon, title, body }: { icon: React.ReactNode; title: string; body: React.ReactNode }) {
  return (
    <div style={{ textAlign: 'center', border: `1px dashed ${LINE}`, borderRadius: 16, padding: '48px 24px', background: '#fff' }}>
      <div style={{ width: 50, height: 50, borderRadius: 15, background: BEIGE, display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: GREEN }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 6 }}>{title}</div>
      <p style={{ fontSize: 14, color: SLATE, maxWidth: 380, margin: '0 auto', lineHeight: 1.6 }}>{body}</p>
    </div>
  )
}

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: 16, boxShadow: '0 1px 2px rgba(0,0,0,.03)' }
const mono: React.CSSProperties = { width: 42, height: 42, borderRadius: 11, background: GREEN, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 15, flex: 'none' }
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }
